import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { ShoppingCart, Minus, Plus, Trash2, ExternalLink, Loader2, Users, AlertCircle } from "lucide-react";
import { useCartStore } from "@/stores/cartStore";
import { useShakatahCartStore } from "@/stores/shakatahCartStore";
import { toast } from "sonner";
import { formatCurrency, Currency } from "@/lib/currency";

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
export function CartDrawer() {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const { 
    items, 
    isLoading, 
    isSyncing,
    updateQuantity, 
    removeItem, 
    createCheckout,
    syncCart
  } = useCartStore();
  
  // Check split cart items
  const splitCartItems = useShakatahCartStore((state) => state.items);
  const splitCartHasItems = splitCartItems.length > 0;
  
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  
  // Get currency from first item, default to USD
  const currency = (items[0]?.price?.currencyCode as Currency) || 'USD';
  
  // Calculate total price with validation
  const totalPrice = items.reduce((sum, item) => {
    const priceAmount = parseFloat(item.price?.amount || '0');
    return sum + (priceAmount * item.quantity);
  }, 0);

  // Sync cart when drawer opens to check if checkout was completed
  useEffect(() => {
    if (isOpen) {
      syncCart();
    }
  }, [isOpen, syncCart]);

  const handleCheckout = async () => {
    try {
      await createCheckout();
      const checkoutUrl = useCartStore.getState().checkoutUrl;
      if (checkoutUrl) {
        window.open(checkoutUrl, '_blank');
        setIsOpen(false);
      }
    } catch (error) {
      console.error('Checkout failed:', error);
      toast.error("Checkout failed", {
        description: "Please try again later."
      });
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" size="icon" className="relative">
          <ShoppingCart className="h-5 w-5" />
          {totalItems > 0 && (
            <Badge className="absolute -top-2 -right-2 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs bg-primary text-primary-foreground">
              {totalItems}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      
      <SheetContent className="w-full sm:max-w-lg flex flex-col h-full">
        <SheetHeader className="flex-shrink-0">
          <SheetTitle>Shopping Cart</SheetTitle>
          <SheetDescription>
            {totalItems === 0 ? "Your cart is empty" : `${totalItems} item${totalItems !== 1 ? 's' : ''} in your cart`}
          </SheetDescription>
        </SheetHeader>
        
        <div className="flex flex-col flex-1 pt-6 min-h-0">
          {items.length === 0 ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <ShoppingCart className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Your cart is empty</p>
              </div>
            </div>
          ) : (
            <>
              {/* Scrollable items area */}
              <div className="flex-1 overflow-y-auto pr-2 min-h-0">
                <div className="space-y-4">
                  {items.map((item) => (
                    <div key={item.variantId} className="flex gap-4 p-2 border-b border-border">
                      <div className="w-16 h-16 bg-secondary rounded-md overflow-hidden flex-shrink-0">
                        {item.product.node.images?.edges?.[0]?.node && (
                          <img
                            src={item.product.node.images.edges[0].node.url}
                            alt={item.product.node.title}
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium truncate text-foreground">{item.product.node.title}</h4>
                        <p className="text-sm text-muted-foreground">
                          {item.selectedOptions.map(option => option.value).join(' • ')}
                        </p>
                        <p className="font-semibold text-foreground">
                          {formatCurrency(item.price?.amount || 0, currency)}
                        </p>
                      </div>
                      
                      <div className="flex flex-col items-end gap-2 flex-shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => removeItem(item.variantId)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                        
                        <div className="flex items-center gap-1">
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => updateQuantity(item.variantId, item.quantity - 1)}
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                          <span className="w-8 text-center text-sm text-foreground">{item.quantity}</span>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => updateQuantity(item.variantId, item.quantity + 1)}
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Fixed checkout section */}
              <div className="flex-shrink-0 space-y-4 pt-4 border-t border-border bg-background">
                {/* Split cart priority prompt */}
                {splitCartHasItems && (
                  <div className="flex items-start gap-3 p-3 bg-primary/10 border border-primary/30 rounded-lg">
                    <Users className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                    <div className="flex-1 space-y-2">
                      <p className="text-sm font-medium text-foreground">
                        You have items in your Split Cart
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Complete your split payment checkout first, then come back for these items.
                      </p>
                      <Button 
                        variant="default" 
                        size="sm" 
                        className="w-full bg-gradient-to-r from-primary to-primary/70"
                        onClick={() => {
                          setIsOpen(false);
                          navigate("/shakatah-checkout");
                        }}
                      >
                        <Users className="w-4 h-4 mr-2" />
                        Checkout Split Cart First
                      </Button>
                    </div>
                  </div>
                )}

                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold text-foreground">Total</span>
                  <span className="text-xl font-bold text-foreground">
                    {formatCurrency(totalPrice, currency)}
                  </span>
                </div>
                
                <Button 
                  onClick={handleCheckout}
                  className="w-full" 
                  size="lg"
                  disabled={items.length === 0 || isLoading || isSyncing || splitCartHasItems}
                >
                  {isLoading || isSyncing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {isSyncing ? 'Syncing Cart...' : 'Creating Checkout...'}
                    </>
                  ) : splitCartHasItems ? (
                    <>
                      <AlertCircle className="w-4 h-4 mr-2" />
                      Complete Split Cart First
                    </>
                  ) : (
                    <>
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Checkout with Shopify
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
